import axios from 'axios';

// Single source of truth for GET /sysparams/v2/params.
// The whole payload (dates, flags, etc.) is fetched at most once per TTL window
// and shared across every consumer (signup dates, cms audio flag, ...).
const TTL = 5 * 60 * 1000; // 5 minutes

// Shared in-flight request so concurrent dispatches reuse one network call
// instead of racing to fetch the same payload.
let inflight = null;

const state = () => ({
  params: null,
  fetchedAt: 0,
  // 'live' | 'preview' — the cache is only valid for the mode it was fetched in,
  // so a tester who logs in after the anonymous boot fetch refetches instead of
  // being served the cached live payload.
  fetchedMode: null,
});

const getters = {
  params: state => state.params,
  getParam: state => name => state.params?.[name] ?? null,
};

const mutations = {
  setParams(state, { params, mode }) {
    state.params = params;
    state.fetchedAt = Date.now();
    state.fetchedMode = mode;
  },
  clearParams(state) {
    state.params = null;
    state.fetchedAt = 0;
    state.fetchedMode = null;
  },
};

const actions = {
  // Returns the sysparams payload. Serves from cache while fresh (< TTL);
  // otherwise fetches once. Concurrent callers share the in-flight request.
  // Pass { force: true } to bypass the cache and refetch immediately.
  async fetchParams({ state, commit, rootState, rootGetters }, { force = false } = {}) {
    // Testers receive preview values via ?preview=1 (app-enforced: only sent for
    // testers). rootGetters is optional-chained so unit tests can pass a minimal
    // context without a profile module.
    const isTester = rootGetters?.['profile/isTester'] === true;
    const mode = isTester ? 'preview' : 'live';

    const isFresh =
      state.params && state.fetchedMode === mode && Date.now() - state.fetchedAt < TTL;
    if (isFresh && !force) return state.params;
    if (inflight) return inflight;

    // Public endpoint; attach auth only when we have it so pre-login callers
    // (e.g. boot-time replay sampling) still work.
    const accessToken = rootState.user.token;
    const url =
      process.env.VUE_APP_API_URL + '/sysparams/v2/params' + (isTester ? '?preview=1' : '');
    inflight = axios
      .get(url, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      })
      .then(({ status, data }) => {
        if (status !== 200) throw new Error('Failed to fetch sysparams');
        commit('setParams', { params: data, mode });
        return data;
      })
      .finally(() => {
        inflight = null;
      });

    return inflight;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
